<script setup>
import { computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

import EnEditableDataFieldsViewSection from 'Components/forms/EnEditableDataFieldsViewSection/EnEditableDataFieldsViewSection.vue';
import ExternalTab from './externalTab.vue';
import { enFetch } from 'Js/utils/fetchUtils';

const {t} = useI18n();
const modelValue = defineModel('modelValue');
const emit = defineEmits(['update:modelValue', 'message-receive']);


const user = computed({
  get() {
    return modelValue.value;
  },

  set(value) {
    emit('update:modelValue', value);
  },
});

// map user object to the fields array expected by EnEditableDataFieldsViewSection
const fields = computed({
  get() {
    const userData = user.value || {};
    return [
      {key: 'Email', fieldLabel: t('Authentication.manage.general.email'), data: userData.Email || '', editable: true},
      {key: 'PhoneNumber', fieldLabel: t('Authentication.manage.general.phone'), data: userData.PhoneNumber || '', editable: true},
      {key: 'FirstName', fieldLabel: t('Authentication.manage.general.firstname'), data: userData.FirstName || '', editable: true},
      {key: 'LastName', fieldLabel: t('Authentication.manage.general.lastname'), data: userData.LastName || '', editable: true},
      {key: 'Description', fieldLabel: t('Authentication.manage.general.description'), data: userData.Description || '', editable: true},
    ];
  },
  set(newFields) {
    const updated = {...(user.value || {})};
    newFields.forEach(f => {
      if (f.key) updated[f.key] = f.data;
    });
    user.value = updated;
  }
});

async function save() {
  const res = await enFetch({url: '/api/Profile/SaveProfile', method: 'post', body: JSON.stringify(user.value)});
  emit('message-receive', await res.text(), res.ok ? 'success' : 'error');
}

async function sendEmailConfirmation() {
  const res = await fetch('/api/Profile/SendVerificationEmail', {method: 'post'});

  emit('message-receive', res.ok ? t('Authentication.manage.general.confirmEmailSent') : await res.text(), res.ok ? 'success' : 'error');
}

async function onUpdateFields(updated) {
  fields.value = updated;
  await nextTick();
  await save();
}

function onEditButtonClicked() {
  emit('message-receive', t('common.editingStarted'), 'info');
}
</script>

<template>
  <div class="general-info-container">
    <en-editable-data-fields-view-section
        class="user-profile-fields"
        :fields="fields"
        :section-heading="t('Authentication.manage.general.title')"
        @update-fields="onUpdateFields"
        @edit-button-clicked="onEditButtonClicked"
    ></en-editable-data-fields-view-section>
    <external-tab v-model:modelValue="user" @message-receive="(msg, type) => emit('message-receive', msg, type)"></external-tab>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.general-info-container {
  max-width: 1200px;
  width: 100%;
  gap: 3rem;
  margin-top: variables.$spacing-l;
  display: flex;
  flex-direction: column;
}

:deep(.user-profile-fields .v-text-field) {
  padding: 0 !important;
}

:deep(.v-input__details) {
  display: none !important;
}

:deep(.user-profile-fields .fields-view-container) {
  grid-template-columns: max-content minmax(300px, 40%);
  column-gap: 3rem;
}

:deep(.user-profile-fields .field-label) {
  padding-right: 1.5rem;
}

@media (max-width: 640px) {
  :deep(.user-profile-fields .fields-view-container) {
    grid-template-columns: 1fr;
    row-gap: 0.5rem;
    padding: 0.5rem;
  }

  :deep(.user-profile-fields .field-label) {
    white-space: normal;
    margin-bottom: 0.125rem;
    padding-right: 0.125rem;
  }
  :deep(.user-profile-fields .field-data) {
    white-space: normal;
    margin-bottom: 0 !important;
  }
}
</style>